import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { ActionsColumn, DetailsCard } from '../helpers/Components';
import { Button, Switch, Select, Input, Modal, Avatar, Row, Col, Tag, Divider, Spin, Tooltip } from 'antd';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import MyTable from '../helpers/MyTable';
import SubPageLayout from '../helpers/SubPageLayout';
import format from 'date-fns/format';
import { useDispatch, useSelector } from 'react-redux';
import {
  isFeatureEnabled,
  getIsSuperAdmin,
  getUser,
  getIsAdminApi,
} from '../../../features/auth.store';
import { DEFAULT_PAGE_SIZE, UPDATE_ON_FORMAT } from '../../../utils/constant';
import {
  enableDisabledSite,
  fetchSiteById,
  getEditingSite,
  getPaginatedSiteList,
  getSiteList,
  getSiteListLoading,
} from '../../../features/site.store';
import { GET } from '../../../service/base-api-service';
import { ROLE } from '../../../utils/features';
import get from 'lodash.get';
import '../SubPages/styles/sites.scss';
import { resizeCloudinaryImages } from '../../../utils/utils';
import FormSection from '../helpers/FormSection';
import FormField from '../helpers/FormField';
import { updateUser } from '../../../features/user.store';
import { toast } from 'react-toastify';
import to from '../../../utils/to';
import { phoneRegEx } from '../../../utils/constant';

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((a) => (typeof a === 'string' ? a : a?.authority)).filter(Boolean)
    : [];

  const splitScope =
    typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return Array.from(
    new Set([
      ...splitScope,
      ...(role ? [role] : []),
      ...(Array.isArray(roles) ? roles : []),
      ...authorityNames,
    ].filter(Boolean)),
  );
};

const hasRole = (user, roleConst) => normalizeRoles(user).includes(roleConst);

const setSearchFocusStyle = (event, focused) => {
  const wrapper = event.currentTarget.closest('.ant-input-affix-wrapper');
  if (!wrapper) return;

  if (focused) {
    wrapper.style.setProperty('border-color', '#009548', 'important');
    wrapper.style.setProperty('box-shadow', '0 0 0 2px rgba(0, 149, 72, 0.12)', 'important');
  } else {
    wrapper.style.removeProperty('border-color');
    wrapper.style.removeProperty('box-shadow');
  }
};

const hasMinearcAuthority = (user) => {
  const roles = normalizeRoles(user);
  return roles.some((r) =>
    [
      'ROLE_TYPE_MINEARC_ROLE',
      'ROLE_MINE_ARC_USER',
      'ROLE_MINE_ARC_SERVICE_TECHNICIAN',
      'ROLE_MINE_ARC_SERVICE_COORDINATOR',
      ROLE.MINEARC,
    ].includes(r),
  );
};

const hasSiteAuthority = (user) => {
  const roles = normalizeRoles(user);
  return roles.some((r) =>
    [
      'ROLE_TYPE_SITE_ROLE',
      'ROLE_SITE_SERVICE_TECHNICIAN',
      ROLE.ST,
      ROLE.SU,
    ].includes(r),
  );
};

const hasTenantAuthority = (user) => {
  const roles = normalizeRoles(user);
  return roles.some((r) =>
    [
      'ROLE_TENANT_ADMIN',
      'ROLE_SERVICE_PROVIDER',
      ROLE.TA,
    ].includes(r),
  );
};

const InfoItem = ({ icon, label, value }) => (
  <div style={styles.infoItem}>
    <div style={styles.infoIcon}>{icon}</div>
    <div style={styles.infoContent}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value || '-'}</div>
    </div>
  </div>
);

const ContactCard = ({ label, contact, onEdit }) => {
  if (!contact) return null;

  return (
    <div style={styles.contactCard}>
      <div style={styles.contactTitle}>
        {label}
        <Button type="link" size="small" onClick={() => onEdit(contact)}>
          Edit
        </Button>
      </div>
      <InfoItem icon={<UserOutlined />} label="Name" value={contact.fullName} />
      <InfoItem icon={<MailOutlined />} label="Email" value={contact.email} />
      <InfoItem icon={<PhoneOutlined />} label="Phone" value={contact.telephoneNumber} />
      <InfoItem icon={<UserOutlined />} label="Role" value={contact?.role?.displayName} />
    </div>
  );
};

const SiteContactEditor = ({ contact, siteId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleSave = async (values) => {
    setLoading(true);
    const [err] = await to(
      dispatch(
        updateUser({
          userId: contact.id,
          body: {
            fullName: values.fullName,
            email: values.email,
            telephoneNumber: values.telephoneNumber,
          },
        }),
      ).unwrap(),
    );

    if (err?.status === 409) {
      toast.error('A user with this email address already exists.');
    } else if (err) {
      toast.error('Could not update. Please try again!');
    } else {
      toast.success('Contact details updated successfully!');
      await dispatch(fetchSiteById({ id: siteId }));
      onClose();
    }
    setLoading(false);
  };

  return (
    <Modal title="Edit Contact Details" visible onCancel={onClose} footer={null} width={640}>
      <Formik
        initialValues={{
          fullName: contact.fullName || '',
          email: contact.email || '',
          telephoneNumber: contact.telephoneNumber || '',
        }}
        onSubmit={handleSave}
        validationSchema={Yup.object({
          fullName: Yup.string().required('Required'),
          email: Yup.string().required('Required').email('Invalid email address'),
          telephoneNumber: Yup.string().required('Required').matches(phoneRegEx, 'Invalid Phone Number'),
        })}
      >
        <Form>
          <FormSection title="Contact Details">
            <FormField labelText="Name" placeholder="Input here" name="fullName" />
            <FormField labelText="Email Address" placeholder="Input here" name="email" />
            <FormField labelText="Telephone Number" placeholder="+64" name="telephoneNumber" />
          </FormSection>
          <div className="d-flex jc-end space-top">
            <Button onClick={onClose} disabled={loading} className="space-right">
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save
            </Button>
          </div>
        </Form>
      </Formik>
    </Modal>
  );
};

const columns = (dispatch, { localAscending, handleChangeOrder, handleOpenSite }) => [
  {
    title: 'Site name',
    dataIndex: 'name',
    render: (name, record) => (
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleOpenSite(record);
        }}
        style={{ cursor: 'pointer', display: 'inline-block' }}
      >
        <DetailsCard
          src={record.imageUrl}
          name={name}
          id={record.id}
          className="d-flex f-row ai-center"
          as="div"
        />
      </div>
    ),
    sorter: true,
    sortDirections: [localAscending ? 'descend' : 'ascend'],
    onHeaderCell: () => ({
      onClick: handleChangeOrder,
      style: { cursor: 'pointer' },
    }),
  },
  {
    title: 'Company',
    dataIndex: 'tenantName',
    render: (value, record) => value || get(record, 'tenant.name') || 'N/A',
  },
  {
    title: 'Update On',
    dataIndex: 'lastModifiedDate',
    render: (data) => (data ? format(new Date(data), UPDATE_ON_FORMAT) : '-'),
  },
  {
    title: 'Enabled',
    dataIndex: 'enabled',
    render: (data, record) => (
      <Switch
        checked={data}
        onClick={(e) => e?.stopPropagation?.()}
        onChange={() => dispatch(enableDisabledSite({ value: !data, id: record.id }))}
      />
    ),
  },
  {
    title: 'Actions',
    render: (data, record) => (
      <div className="site-row-actions" onClick={(e) => e.stopPropagation()}>
        <ActionsColumn showEdit editUrl={`/sites/${record.id}/edit`} />
        <Tooltip title="View site">
          <Button
            type="link"
            className="site-view-action"
            aria-label={`View ${record.name}`}
            icon={<EyeOutlined />}
            onClick={() => handleOpenSite(record)}
          />
        </Tooltip>
      </div>
    ),
    width: 150,
  },
];

const Sites = () => {
  const dispatch = useDispatch();

  const getFeatureEnable = useSelector(isFeatureEnabled);
  const featureAddSites = getFeatureEnable('add_sites');

  const user = useSelector(getUser);
  const isSuperAdmin = useSelector(getIsSuperAdmin);
  const isAdminApi = useSelector(getIsAdminApi);

  const siteList = useSelector(getSiteList);
  const siteListLoading = useSelector(getSiteListLoading);
  const editingSite = useSelector(getEditingSite);

  const [allSites, setAllSites] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);

  const [searchText, setSearchText] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [localAscending, setLocalAscending] = useState(true);

  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  const isMinearcRole = useMemo(() => hasMinearcAuthority(user), [user]);
  const isSuperAdminRole = useMemo(() => hasRole(user, ROLE.SA), [user]);
  const isSiteRole = useMemo(() => !isMinearcRole && hasSiteAuthority(user), [user, isMinearcRole]);
  const isTenantRole = useMemo(() => !isMinearcRole && hasTenantAuthority(user), [user, isMinearcRole]);

  const hasGlobalSiteAccess = useMemo(
    () => isSuperAdmin || isSuperAdminRole || isMinearcRole || isAdminApi,
    [isSuperAdmin, isSuperAdminRole, isMinearcRole, isAdminApi],
  );

  const canAddSites = featureAddSites || hasGlobalSiteAccess;

  const loadCompanies = useCallback(async () => {
    if (!hasGlobalSiteAccess) return;

    try {
      const payload = await GET('v1/admin/tenant/minimal');
      const options = (Array.isArray(payload) ? payload : [])
        .filter((item) => item && item.identifier)
        .map((item) => ({
          value: item.identifier,
          label: item.name || item.identifier,
        }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));

      setCompanyOptions(options);
    } catch (err) {
      console.error('SITES: failed to load company filter options', err);
      setCompanyOptions([]);
    }
  }, [hasGlobalSiteAccess]);

  const loadSites = useCallback(async () => {
    if (hasGlobalSiteAccess) {
      setGlobalLoading(true);
      try {
        const payload = await GET('v1/admin/site', {
          params: {
            page: 0,
            size: 10000,
            sortColumn: 'name',
            isAscending: true,
            searchKey: '',
            tenantId: undefined,
          },
        });

        setAllSites(payload?.results || []);
      } catch (err) {
        console.error('SITES: failed to load global sites', err);
        setAllSites([]);
      } finally {
        setGlobalLoading(false);
      }
    } else {
      dispatch(getPaginatedSiteList({ page: 0 }));
    }
  }, [dispatch, hasGlobalSiteAccess]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const sourceSites = hasGlobalSiteAccess ? allSites : siteList;

  const filteredSites = useMemo(() => {
    let list = [...(sourceSites || [])];

    if (hasGlobalSiteAccess && selectedCompanyFilter) {
      list = list.filter(
        (site) =>
          site?.tenantIdentifier === selectedCompanyFilter ||
          site?.tenant?.identifier === selectedCompanyFilter,
      );
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();

      list = list.filter((site) => {
        const name = site?.name?.toLowerCase() || '';
        const company = (site?.tenantName || site?.tenant?.name || '').toLowerCase();
        const address = site?.address?.toLowerCase() || '';

        const contacts = [
          site?.mainContact?.fullName,
          site?.secondContact?.fullName,
          site?.thirdContact?.fullName,
          site?.fourthContact?.fullName,
          site?.mainContact?.email,
          site?.secondContact?.email,
          site?.thirdContact?.email,
          site?.fourthContact?.email,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return (
          name.includes(q) ||
          company.includes(q) ||
          address.includes(q) ||
          contacts.includes(q)
        );
      });
    }

    list.sort((a, b) => {
      const aName = (a?.name || '').toLowerCase();
      const bName = (b?.name || '').toLowerCase();

      if (localAscending) return aName.localeCompare(bName);
      return bName.localeCompare(aName);
    });

    return list;
  }, [sourceSites, hasGlobalSiteAccess, selectedCompanyFilter, searchText, localAscending]);

  const paginatedSites = useMemo(() => {
    const start = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const end = start + DEFAULT_PAGE_SIZE;
    return filteredSites.slice(start, end);
  }, [filteredSites, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedCompanyFilter]);

  const handleChangeOrder = () => {
    setLocalAscending((prev) => !prev);
  };

  const handleOpenSite = async (site) => {
    setSelectedSiteId(site?.id);
    setSiteModalOpen(true);

    if (site?.id) {
      await dispatch(fetchSiteById({ id: site.id }));
    }
  };

  const handleCloseSiteModal = () => {
    setSiteModalOpen(false);
    setSelectedSiteId(null);
    setEditingContact(null);
  };

  const activeLoading = hasGlobalSiteAccess ? globalLoading : siteListLoading;
  const modalLoading = siteModalOpen && (!editingSite || editingSite?.id !== selectedSiteId);

  if (isSiteRole && !hasGlobalSiteAccess) {
    return null;
  }

  return (
    <SubPageLayout
      title="Sites"
      extra={
        canAddSites && (
          <Link to="/sites/add">
            <Button type="primary" icon={<PlusOutlined />}>
              Add Site
            </Button>
          </Link>
        )
      }
    >
      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
            placeholder="Search Sites"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
              onFocus={(event) => setSearchFocusStyle(event, true)}
              onBlur={(event) => setSearchFocusStyle(event, false)}
          />
        </div>

        {hasGlobalSiteAccess && (
          <div style={styles.dropdownWrap}>
            <Select
              allowClear
              showSearch
              className="sites-company-filter"
              size="large"
              optionFilterProp="label"
              value={selectedCompanyFilter}
              options={companyOptions}
              style={{ width: '100%' }}
              placeholder="Filter by Company"
              onChange={(val) => setSelectedCompanyFilter(val || undefined)}
              dropdownStyle={{ borderRadius: 12 }}
            />
          </div>
        )}
      </div>

      <MyTable
        columns={columns(dispatch, {
          localAscending,
          handleChangeOrder,
          handleOpenSite,
        })}
        dataSource={paginatedSites}
        pagination={{
          position: ['none', 'bottomCenter'],
          pageSize: DEFAULT_PAGE_SIZE,
          total: filteredSites.length,
          current: currentPage,
        }}
        onChange={(pagination) => {
          setCurrentPage(pagination.current);
        }}
        loading={activeLoading}
        rowKey="id"
      />

      <Modal
        visible={siteModalOpen}
        onCancel={handleCloseSiteModal}
        footer={null}
        width={1180}
        destroyOnClose={false}
        bodyStyle={{ padding: 0 }}
        style={{ top: 24 }}
      >
        <div style={styles.modalWrap}>
          {modalLoading ? (
            <div style={styles.modalLoadingWrap}>
              <Spin size="large" />
            </div>
          ) : editingSite ? (
            <>
              <div style={styles.modalHero}>
                <div style={styles.modalHeroLeft}>
                  <div style={styles.heroImageWrap}>
                    <div style={styles.heroImageFrame}>
                      <Avatar
                        shape="square"
                        size={112}
                        src={
                          editingSite.imageUrl
                            ? resizeCloudinaryImages(editingSite.imageUrl, 240, 240)
                            : undefined
                        }
                        style={styles.heroAvatar}
                      >
                        {(editingSite.name || 'S').charAt(0)}
                      </Avatar>
                    </div>
                  </div>

                  <div style={styles.heroText}>
                    <div style={styles.heroTopRow}>
                      <div style={styles.heroTitleBlock}>
                        <div style={styles.heroTitle}>{editingSite.name || 'Site Details'}</div>
                        <div style={styles.heroSubtitle}>
                          {editingSite.tenantName || get(editingSite, 'tenant.name') || 'No company'}
                        </div>
                      </div>

                      <Tag color={editingSite.enabled ? 'green' : 'default'} style={styles.statusTag}>
                        {editingSite.enabled ? 'Enabled' : 'Disabled'}
                      </Tag>
                    </div>

                    <div style={styles.heroMetaRow}>
                      <div style={styles.heroMetaItem}>
                        <EnvironmentOutlined />
                        <span>{editingSite.address || 'No address'}</span>
                      </div>
                      <div style={styles.heroMetaItem}>
                        <CalendarOutlined />
                        <span>
                          {editingSite.lastModifiedDate
                            ? format(new Date(editingSite.lastModifiedDate), UPDATE_ON_FORMAT)
                            : 'No update date'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.modalBody}>
                <Row gutter={[20, 20]}>
                  <Col xs={24} lg={10}>
                    <div style={styles.sectionCard}>
                      <div style={styles.sectionTitle}>Site Information</div>
                      <Divider style={{ margin: '10px 0 16px' }} />

                      <div style={styles.infoGrid}>
                        <InfoItem icon={<UserOutlined />} label="Site Name" value={editingSite.name} />
                        <InfoItem
                          icon={<UserOutlined />}
                          label="Company"
                          value={editingSite.tenantName || get(editingSite, 'tenant.name')}
                        />
                        <InfoItem
                          icon={<EnvironmentOutlined />}
                          label="Address"
                          value={editingSite.address}
                        />
                        <InfoItem
                          icon={<CalendarOutlined />}
                          label="Updated On"
                          value={
                            editingSite.lastModifiedDate
                              ? format(new Date(editingSite.lastModifiedDate), UPDATE_ON_FORMAT)
                              : '-'
                          }
                        />
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} lg={14}>
                    <div style={styles.contactOverviewCard}>
                      <div style={styles.sectionTitle}>Contact Details</div>
                      <Divider style={{ margin: '10px 0 16px' }} />

                      {[editingSite.mainContact, editingSite.secondContact, editingSite.thirdContact, editingSite.fourthContact].filter(Boolean).length ? (
                        <Row gutter={[14, 14]}>
                          {[
                            { label: 'First Contact', data: editingSite.mainContact },
                            { label: 'Second Contact', data: editingSite.secondContact },
                            { label: 'Third Contact', data: editingSite.thirdContact },
                            { label: 'Fourth Contact', data: editingSite.fourthContact },
                          ]
                            .filter((item) => item.data)
                            .map((item, index) => (
                              <Col xs={24} md={12} key={`${item.data?.id || index}_${item.label}`}>
                                <ContactCard
                                  label={item.label}
                                  contact={item.data}
                                  onEdit={setEditingContact}
                                />
                              </Col>
                            ))}
                        </Row>
                      ) : (
                        <div style={styles.emptyState}>No contacts assigned to this site.</div>
                      )}
                    </div>
                  </Col>

                  <Col span={24}>
                    <div style={styles.bottomBar}>
                      <div style={styles.bottomStatus}>
                        <Tag color={editingSite.enabled ? 'green' : 'default'} style={{ margin: 0 }}>
                          {editingSite.enabled ? 'Site Enabled' : 'Site Disabled'}
                        </Tag>
                      </div>

                      <div style={styles.bottomActions}>
                        <Button onClick={handleCloseSiteModal} size="large">
                          Close
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          ) : (
            <div style={styles.modalLoadingWrap}>
              <div>No site found.</div>
            </div>
          )}
        </div>
      </Modal>
      {editingContact && selectedSiteId ? (
        <SiteContactEditor
          contact={editingContact}
          siteId={selectedSiteId}
          onClose={() => setEditingContact(null)}
        />
      ) : null}
    </SubPageLayout>
  );
};

export default Sites;

const styles = {
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
    alignItems: 'stretch',
  },

  searchWrap: {
    flex: '0 0 420px',
    width: 420,
    minWidth: 420,
    maxWidth: 420,
  },

  dropdownWrap: {
    flex: '0 0 420px',
    width: 420,
    minWidth: 420,
    maxWidth: 420,
  },

  modalWrap: {
    background: '#f7f9fb',
    borderRadius: 20,
    overflow: 'hidden',
    minHeight: 640,
  },

  modalLoadingWrap: {
    minHeight: 640,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f7f9fb',
  },

  modalHero: {
    padding: '28px 30px 24px',
    background: 'linear-gradient(135deg, #ffffff 0%, #f4f8f5 100%)',
    borderBottom: '1px solid #edf1f5',
  },

  modalHeroLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 26,
    flexWrap: 'wrap',
  },

  heroImageWrap: {
    flexShrink: 0,
  },

  heroImageFrame: {
    width: 120,
    height: 120,
    padding: 6,
    background: '#ffffff',
    borderRadius: 18,
    boxShadow: '0 12px 30px rgba(16, 24, 40, 0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },

  heroAvatar: {
    background: '#e8f5ec',
    color: '#0b6a3b',
    fontWeight: 800,
    fontSize: 30,
  },

  heroText: {
    minWidth: 0,
    flex: 1,
  },

  heroTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },

  heroTitleBlock: {
    minWidth: 0,
    flex: 1,
  },

  heroTitle: {
    fontSize: 30,
    fontWeight: 800,
    color: '#17212b',
    lineHeight: 1.15,
  },

  heroSubtitle: {
    marginTop: 8,
    fontSize: 15,
    color: '#5b6570',
    fontWeight: 500,
  },

  statusTag: {
    margin: 0,
    fontWeight: 700,
    paddingInline: 12,
    height: 30,
    lineHeight: '28px',
    borderRadius: 999,
  },

  heroMetaRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 18,
  },

  heroMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: '#fff',
    border: '1px solid #ecf0f3',
    borderRadius: 12,
    color: '#475467',
    fontSize: 13,
    fontWeight: 600,
  },

  modalBody: {
    padding: 24,
  },

  sectionCard: {
    background: '#fff',
    border: '1px solid #edf1f5',
    borderRadius: 18,
    padding: 20,
    height: '100%',
    boxShadow: '0 4px 18px rgba(16, 24, 40, 0.05)',
  },

  contactOverviewCard: {
    background: '#fff',
    border: '1px solid #edf1f5',
    borderRadius: 18,
    padding: 20,
    boxShadow: '0 4px 18px rgba(16, 24, 40, 0.05)',
  },

  contactCard: {
    background: '#fafcfd',
    border: '1px solid #eef2f6',
    borderRadius: 16,
    padding: 14,
    height: '100%',
    display: 'grid',
    gap: 10,
  },

  contactTitle: {
    alignItems: 'center',
    fontSize: 14,
    fontWeight: 800,
    color: '#17212b',
    display: 'flex',
    justifyContent: 'space-between',
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#17212b',
  },

  infoGrid: {
    display: 'grid',
    gap: 14,
  },

  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    background: '#fff',
    border: '1px solid #eef2f6',
    borderRadius: 14,
  },

  infoIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'rgba(11, 106, 59, 0.08)',
    color: '#0b6a3b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 15,
  },

  infoContent: {
    minWidth: 0,
    flex: 1,
  },

  infoLabel: {
    fontSize: 11,
    color: '#7b8794',
    marginBottom: 4,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },

  infoValue: {
    fontSize: 14,
    color: '#1f2933',
    fontWeight: 600,
    wordBreak: 'break-word',
  },

  emptyState: {
    color: '#7b8794',
    fontStyle: 'italic',
    minHeight: 120,
    display: 'flex',
    alignItems: 'center',
  },

  bottomBar: {
    background: '#fff',
    border: '1px solid #edf1f5',
    borderRadius: 18,
    padding: 18,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },

  bottomStatus: {
    display: 'flex',
    alignItems: 'center',
  },

  bottomActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
};